import * as angular from 'angular';

import { PreviewMode, ScriptType } from '../../models/enum';
import { IPdfToSvgService, IFilePathsHelper } from '../helpers';
import { Directory, IUploadedFileData } from '../../models';
import { IRequestService, ILoggerService, IUploadService } from '../../services';

export interface IFileManagementService {
    getStructure(): angular.IPromise<Directory>;
    getContent(): angular.IPromise<Directory>;
    getContent(applicationGuid: string): angular.IPromise<Directory>;
    upload(file: File, destination: string, newName?: string, applicationGuid?: string): angular.IPromise<string[]>;
    deleteFile(relativePath: string, fileName: string): angular.IPromise<void>;
    download(relativePath: string, fileName: string): angular.IPromise<string>;
    read(relativePath: string): angular.IPromise<Int8Array>;
    read(relativePath: string, applicationGuid: string): angular.IPromise<Int8Array>;
    read(relativePath: string, applicationGuid: string): angular.IPromise<Int8Array>;
    getPreviewMode(fileName: string): PreviewMode;
    getFileFromContentAsUrl(fileName: string): angular.IPromise<string>;
    getFileFromContentAsUrl(fileName: string, applicationGuid: string): angular.IPromise<string>;
    getFileFromContentAsUrl(fileName: string, applicationGuid: string): angular.IPromise<string>;
    write(relativePath: string, fileData: string): angular.IPromise<string[]>;
    getAllScriptLibraries(type: ScriptType): angular.IPromise<string[]>;
}

export class FileManagementService implements IFileManagementService {
    static serviceId = 'fileManagementService';

    static $inject = [
        '$http',
        '$q',
        'pdfToSvgService',
        'loggerService',
        'filePathsHelper',
        'uploadService',
        'requestService'
    ];
    
    constructor(
        private _$http: angular.IHttpService,
        private _$q: angular.IQService,
        private _pdfToSvgService: IPdfToSvgService,
        private _loggerService: ILoggerService,
        private _filePathsHelper: IFilePathsHelper,
        private _upload: IUploadService,
        private _requestService: IRequestService
    ) {
    }

    private getControllerUrl(): string {
        return this._requestService.getUrlV1() + 'fileManagement';
    }

    getStructure(): angular.IPromise<Directory> {
        const url = this.getControllerUrl();
        return this._requestService.get(url);
    }

    getContent(applicationGuid?: string): angular.IPromise<Directory> {
        let url: string;
        if (applicationGuid) {
            url = `${this.getControllerUrl()}/contentByApplication/${applicationGuid}`;
        } else {
            url = this.getControllerUrl() + '/content';
        }
        this._requestService.cleanCache(url);
        return this._requestService.get(url);
    }

    upload(file: File, destination: string, newName?: string, applicationGuid?: string): angular.IPromise<string[]> {
        let url = this._requestService.getUrlV1() + 'fileManagement/upload';
        if (applicationGuid) {
            url += 'forApplication/' + applicationGuid;
        }
        const uploadedData = {
            url: url,
            file: file,
            data: {
                uploadParams:
                {
                    newName: newName ? newName : file.name,
                    relativePath: destination
                }
            }
        } as IUploadedFileData;
        return this._upload
            .uploadData<string[]>(uploadedData)
            .then(response => {
                this._loggerService.logSuccess('File uploaded successfully', true);
                this._requestService.cleanCacheAtV1('fileManagement');
                this._requestService.cleanCacheAtV1('thermoPackage');
                this._requestService.cleanCacheAtV1('fileManagement/getScriptLibraries');
                this._requestService.cleanCacheAtV1('group/');
                if (response && response.length > 0) {
                    response.forEach(d => this._requestService.cleanCacheAtV1(`group/${d}`));
                }
                return response;
            });
    }

    deleteFile(relativePath: string, fileName: string): angular.IPromise<void> {
        const url = this._requestService.getUrlV1() + 'fileManagement/deleteFile';
        const data = {
            relativePath: relativePath,
            fileName: fileName
        };
        return this._requestService.delete(url, data)
            .then(() => {
                this._requestService.cleanCacheAtV1('fileManagement');
                this._requestService.cleanCacheAtV1('thermoPackage');
                this._requestService.cleanCacheAtV1('fileManagement/getScriptLibraries');
                this._requestService.cleanCacheAtV1('group/');
            });
    }

    download(relativePath: string, fileName: string): angular.IPromise<string> {
        const defered = this._$q.defer<string>();
        const url = this._requestService.getUrlV1() + 'fileManagement/download';
        const data = { relativePath: relativePath, fileName: fileName };
        this._$http
            .post(url, data)
            .then(data => {
                defered.resolve(this._requestService.getApiUrl() + data.data);
            })
            .catch(reason => {
                defered.reject(reason);
            });
        return defered.promise;
    }

    read(relativePath: string, applicationGuid?: string): angular.IPromise<Int8Array> {
        const url = this.getUrl(applicationGuid);
        const data = angular.toJson(relativePath);
        return this._requestService.post(url, data);
    }

    write(relativePath: string, fileData: string): angular.IPromise<string[]> {
        const url = 'fileManagement/updateFile';
        const data = {
            fileName: relativePath,
            data: this.b64EncodeUnicode(fileData)
        };
        return this._requestService
            .postAtV1<string[]>(url, data)
            .then((response: string[]) => {
                this._requestService.cleanCacheAtV1('group/');
                if (response && response.length > 0) {
                    response.forEach(d => this._requestService.cleanCacheAtV1(`group/${d}/`));
                }
                return response;
            });
    }

    getFileFromContentAsUrl(fileName: string, applicationGuid?: string): angular.IPromise<string> {
        const mode = this.getPreviewMode(fileName);
        if (mode === PreviewMode.Pdf) {
            return this.getPdfFromContentAsUrl(fileName);
        } else {
            return this.getAnyFileFromContentAsUrl(fileName, mode, applicationGuid);
        }
    }

    private getUrl(applicationGuid: string) {
        const url = applicationGuid
            ? this._requestService.getUrlV1() + 'fileManagement/previewForApplication/' + applicationGuid
            : this._requestService.getUrlV1() + 'fileManagement/preview';
        return url;
    }

    private getAnyFileFromContentAsUrl(fileName: string, previewMode: PreviewMode, applicationGuid?: string): angular.IPromise<string> {
        return this.read(`${fileName}`, applicationGuid)
            .then(data => this.urlBase64(data, previewMode));
    }

    private getPdfFromContentAsUrl(fileName: string): angular.IPromise<string> {
        if (fileName.indexOf('Content') === 0) {
            fileName = fileName.replace('Content', '');
        }
        fileName = fileName.replace(new RegExp('\\\\', 'g'), '/');
        let baseUrl = this._filePathsHelper.getContentUrl();
        if (baseUrl[baseUrl.length - 1] === '/') {
            baseUrl = baseUrl.substr(0, baseUrl.length - 1);
        }
        const url = baseUrl + (fileName[0] === '/' ? '' : '/') + fileName;
        
        return this._pdfToSvgService
            .convertToBase64(url)
            .then(data => this.urlBase64(data, PreviewMode.Pdf));
    }

    getPreviewMode(fileName: string): PreviewMode {
        let mode = PreviewMode.Unknown;

        const ext = fileName.split('.').pop().toLowerCase();
        if (['csv', 'txt', 'xml', 'json'].indexOf(ext) > -1) {
            mode = PreviewMode.Text;
        } else if (['py'].indexOf(ext) > -1) {
            mode = PreviewMode.Python;
        } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'ico'].indexOf(ext) > -1) {
            mode = PreviewMode.Image;
        } else if (['svg'].indexOf(ext) > -1) {
            mode = PreviewMode.Svg;
        } else if (['pdf'].indexOf(ext) > -1) {
            mode = PreviewMode.Pdf;
        }
        return mode;
    }

    private urlBase64(data, mode: PreviewMode): string {
        let url: string;
        if (mode === PreviewMode.Text || mode === PreviewMode.Python) {
            url = this.b64DecodeUnicode(data);
        } else if (mode === PreviewMode.Image) {
            url = `data:image/png;base64,${data}`;
        } else if (mode === PreviewMode.Svg) {
            url = `data:image/svg+xml;base64,${data}`;
        } else if (mode === PreviewMode.Pdf) {
            url = `${data}`;
        }
        return url;
    }

    /*
     * Implementation taken from
     * https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
     */
    private b64EncodeUnicode(str): string {
        return btoa(encodeURIComponent(str).replace(
            /%([0-9A-F]{2})/g,
            (match, p1) => String.fromCharCode(parseInt(p1, 16))
        ));
    }

    private b64DecodeUnicode(str): string {
        try {
            return decodeURIComponent(Array.prototype.map.call(atob(str),
                (c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`
            ).join(''));
        } catch {
            return 'File decoding failed, check file and try to convert it to UTF-8';
        }

    }

    getAllScriptLibraries(type: ScriptType): angular.IPromise<string[]> {
        return this._requestService.getAtV1(`fileManagement/getScriptLibraries?type=${type}`);
    }
}